import React, { useCallback, useEffect } from 'react';
import styled from 'astroturf/react';

import ImpressionWatcher from 'js/utils/impressions/ImpressionWatcher';

import { Page } from './Page';
import { BuildVuPage, getManifest } from './api';
import { useAttachmentShown } from './attachment_shown_store';
import { useScribdEvents } from './scribd_events_tracker';
import _Container from "Attachment-Container.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/liswp/build_vu/Attachment.tsx?styleId=Container";


// Only preload 1 page on mobile - this empirically reduces INP.
// (Probably because mobile devices are generally lower-powered in both network and rendering.)
const isMobile =
  window.innerWidth <= 768 || window.matchMedia('(max-width: 767px)').matches;

const PRELOAD_PAGES = isMobile ? 1 : 3;

const Container = /*#__PURE__*/styled("div", null, {
  displayName: "Container",
  styles: _Container
});

type PageSpec = {
  page: BuildVuPage;
  load: boolean;
};

export type AttachmentProps = {
  attachmentId: string;
  shown?: boolean;
  pages?: number;
};

export const Attachment: React.FC<AttachmentProps> = ({
  attachmentId,
  shown: forceShown,
  pages,
}) => {
  const [pageSpecs, setPageSpecs] = React.useState<PageSpec[] | null>(null);

  const { enqueueEvent } = useScribdEvents(attachmentId);

  // TODO: Decouple useAttachmentShown
  const statefulShown = useAttachmentShown();
  const shown = forceShown ?? statefulShown;

  // Check if this needs alreadyShown
  useEffect(() => {
    if (pageSpecs) return;

    if (shown) {
      getManifest(attachmentId)
        .then((manifest) => {
          setPageSpecs(
            manifest.map((page) => ({
              page,
              load: page.page_number < PRELOAD_PAGES + 1,
            })),
          );
        })
        .catch(console.error);
    }
  }, [attachmentId, pageSpecs, shown]);

  const onIntersect = useCallback(
    (pageNumber: number) => {
      // When a page comes into view, set its load flag to true, as well as the
      // PRELOAD_PAGES number of pages above and below it

      if (!pageSpecs) return;

      let changed = false;
      const newPageSpecs = pageSpecs.map((spec) => {
        if (
          spec.page.page_number >= pageNumber - PRELOAD_PAGES &&
          spec.page.page_number <= pageNumber + PRELOAD_PAGES &&
          !spec.load
        ) {
          changed = true;
          return { ...spec, load: true };
        } else {
          return spec;
        }
      });

      if (changed) {
        setPageSpecs(newPageSpecs);
        enqueueEvent({
          event: 'pageChanged',
          page: pageNumber,
        });
      }
    },
    [enqueueEvent, pageSpecs],
  );

  return (
    <Container
      id="scribd-attachment-container"
      style={{ display: shown ? 'block' : 'none' }}
    >
      {pageSpecs?.slice(0, pages).map(({ page, load }) => (
        <ImpressionWatcher
          key={page.page_number}
          entity_id={`${attachmentId}${page.page_number}`}
          entity_type={17}
          source="build_vu_attachment_page"
          extra={{ attachment_id: attachmentId, page: page.page_number }}
          stopPropagation={false}
        >
          <Page page={page} load={load} onIntersect={onIntersect} />
        </ImpressionWatcher>
      ))}
    </Container>
  );
};
